import { Parser } from './parser';
import { Evaluator } from './evaluator';
import { createRegistry, FunctionRegistry } from './functions';
import { ASTNode, FormulaError, FormulaValue } from './types';
import { getAllLanguagePacks } from './i18n';

export interface EngineResult {
    value: number;
    stringValue?: string | null;
    error: boolean;
    errorMessage?: string;
}

// Singleton registry with all functions pre-registered
let _registry: FunctionRegistry | null = null;

// AST cache to avoid re-parsing the same expressions
const _astCache = new Map<string, ASTNode>();
const AST_CACHE_MAX_SIZE = 1000;

function getRegistry (): FunctionRegistry {
    if (!_registry) {
        _registry = createRegistry();
        // Register aliases for ALL languages at once so any formula evaluates correctly
        const allPacks = getAllLanguagePacks();
        for (const pack of Object.values(allPacks)) {
            _registry.registerLanguage(pack.functions);
        }
    }
    return _registry;
}

function toNumber (val: FormulaValue): number {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    if (typeof val === 'boolean') return val ? 1 : 0;
    if (typeof val === 'string') {
        const n = Number(val.replace(',', '.'));
        return isNaN(n) ? 0 : n;
    }
    if (Array.isArray(val)) {
        return val.length > 0 ? val[0] : 0;
    }
    return 0;
}

function evaluateOne (
    expression: string,
    variables: Map<string, number | number[] | string | null>,
    registry: FunctionRegistry
): EngineResult {
    try {
        const expr = (expression ?? '0').toString().trim();
        if (!expr) {
            return { value: 0, error: false };
        }

        // If it's just a plain number, return it directly
        const asNum = Number(expr.replace(',', '.'));
        if (!isNaN(asNum) && expr.trim() !== '') {
            return { value: asNum, error: false };
        }

        // Prepend = if not already present
        const formula = expr.startsWith('=') ? expr : `=${expr}`;

        let ast = _astCache.get(formula);
        if (!ast) {
            ast = Parser.parse(formula);
            if (_astCache.size >= AST_CACHE_MAX_SIZE) _astCache.clear();
            _astCache.set(formula, ast);
        }
        const evaluator = new Evaluator(variables, registry);
        const result = evaluator.evaluate(ast);
        const numResult = toNumber(result);

        let stringValue: string | null = null;
        if (typeof result === 'string') {
            const parsed = Number(result.replace(',', '.'));
            if (isNaN(parsed)) {
                stringValue = result;
            }
        } else if (typeof result === 'boolean') {
            stringValue = result ? 'TRUE' : 'FALSE';
        }

        return {
            value: isNaN(numResult) ? 0 : numResult,
            stringValue,
            error: false
        };
    } catch (error) {
        if (error instanceof FormulaError) {
            return {
                value: 0,
                error: true,
                errorMessage: `${error.type}: ${error.message}`
            };
        }
        const errorMessage = (error as Error)?.message ?? 'Unknown error';
        return {
            value: 0,
            error: true,
            errorMessage
        };
    }
}

/**
 * Evaluate a single expression with the given variables.
 */
export function evaluate (
    expression: string,
    variables: Map<string, number | number[] | string | null>,
    lang: string = 'pt'
): EngineResult {
    const registry = getRegistry();
    return evaluateOne(expression, variables, registry);
}

/**
 * Evaluate multiple expressions with the same variables.
 * More efficient than calling evaluate() for each one.
 */
export function evaluateBatch (
    expressions: string[],
    variables: Map<string, number | number[] | string | null>,
    lang: string = 'pt'
): EngineResult[] {
    if (expressions.length === 0) return [];

    const registry = getRegistry();
    return expressions.map(expr => evaluateOne(expr, variables, registry));
}

export { FormulaError, FormulaErrorType } from './types';
export type { FormulaValue } from './types';
